import { computed, ref } from 'vue';
import { useMapGetter } from 'dashboard/composables/store';
import { vePerfilTudo } from 'dashboard/helper/fandataPerfis';

// "Ver como": pré-visualização de perfil, para quem administra conferir o que cada um enxerga.
//
// Nasceu do pedido da Tamara em 01/10 ("quero confirmar a visão dessas telas"). O caminho que
// existia era ela se rebaixar a Backoffice de verdade, o que troca o papel nativo, derruba o
// próprio acesso de administrador e exige outra pessoa para desfazer. Pré-visualizar não muda
// dado nenhum: vive no navegador de quem olha, sai com um clique, e some ao trocar de máquina.
//
// ⚠️ É SÓ DESENHO DE TELA, como todo o mapa de perfis. Não rebaixa permissão: quem está vendo
// como Agente continua podendo tudo o que podia na API. Serve para conferir o que o outro vê,
// não para testar bloqueio.
const CHAVE_PREVIA = 'fandata_ver_como';

const lerPrevia = () => {
  try {
    return localStorage.getItem(CHAVE_PREVIA) || null;
  } catch (e) {
    return null;
  }
};

// Fora da função para o estado ser o mesmo em toda a tela: barra lateral e aviso precisam
// concordar, e cada chamada criando o seu `ref` faria o aviso aparecer sem o menu mudar.
const previa = ref(lerPrevia());

export const verComo = perfil => {
  previa.value = perfil;
  try {
    if (perfil) localStorage.setItem(CHAVE_PREVIA, perfil);
    else localStorage.removeItem(CHAVE_PREVIA);
  } catch (e) {
    // Navegador sem armazenamento: a pré-visualização vale só enquanto a página estiver aberta.
  }
};

// T-145 — perfil FanData (agente, backoffice, admin).
//
// O clube pediu três perfis no documento de validação de 31/08 e o Chatwoot community tem dois.
// Perfil customizado vive no enterprise, que desligamos por causa da licença, então o perfil é
// nosso: ele não substitui o papel do Chatwoot, refina quem é agente.
//
// ⚠️ Isto decide o que APARECE. Não é segurança: quem quiser continua alcançando a API. As regras
// que precisam valer de verdade estão no servidor (política de exclusão, bloqueio de contato), e
// é lá que elas são cobradas.
export function useFandataPerfil() {
  const currentUser = useMapGetter('getCurrentUser');
  const currentAccountId = useMapGetter('getCurrentAccountId');

  // O perfil de verdade, gravado na conta.
  const perfilReal = computed(() => {
    const contas = currentUser.value?.accounts || [];
    const conta = contas.find(c => c.id === Number(currentAccountId.value));
    return conta?.fandata_perfil || 'agente';
  });

  // O papel NATIVO, que é o que decide se a pessoa pode pré-visualizar e o que garante a saída
  // de emergência: quem é administrador nunca perde a entrada de Agentes, mesmo vendo como outro
  // perfil, senão ficaria sem como desfazer.
  const papelNativo = computed(() => {
    const contas = currentUser.value?.accounts || [];
    const conta = contas.find(c => c.id === Number(currentAccountId.value));
    return conta?.role || 'agent';
  });

  const podePreVisualizar = computed(() => papelNativo.value === 'administrator');
  const preVisualizando = computed(() =>
    podePreVisualizar.value ? previa.value : null
  );

  const perfil = computed(() => preVisualizando.value || perfilReal.value);

  // Os dois perfis que veem tudo, que é a lista de `PERFIS_TOTAIS` no mapa da barra lateral.
  //
  // ⚠️ Até 02/10 isto olhava só 'admin', e o Desenvolvedor, que é o mais amplo dos seis, perdia
  // os controles de supervisão que dependem desta linha (filtro da fila, reabrir conversa
  // resolvida, blocos de contato). Curadoria de IA e Analista de CS continuam de fora de
  // propósito: o que é deles é relatório e configuração, não a fila inteira.
  const ehAdmin = computed(() => vePerfilTudo(perfil.value));
  const ehBackoffice = computed(() => perfil.value === 'backoffice');
  // Agente e backoffice compartilham quase toda a tela: o backoffice é o agente com as pastas
  // de pendência a mais.
  const ehAtendimento = computed(() => !ehAdmin.value);

  return {
    perfil,
    perfilReal,
    papelNativo,
    podePreVisualizar,
    preVisualizando,
    verComo,
    ehAdmin,
    ehBackoffice,
    ehAtendimento,
  };
}
